import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HeartHandshake, Plus } from "lucide-react";
import api, { formatINR, formatError, formatDate } from "../lib/api";
import { PageHeader, EmptyState, Loader } from "../components/bits";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const CATEGORIES = ["Medical Emergency", "Family Emergency", "Accident", "Job Loss", "Education", "Natural Disaster", "Other"];
export const URGENCY = { critical: "bg-rose-600 text-white", high: "bg-orange-100 text-orange-800", medium: "bg-amber-100 text-amber-800" };

const EMPTY = { title: "", beneficiary: "", category: CATEGORIES[0], urgency: "high", description: "", target_amount: "", upi_id: "", bank_details: "", contact: "" };

const NewHelpDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/help", { ...f, target_amount: Number(f.target_amount) });
      toast.success("Help request posted to the brotherhood");
      setOpen(false);
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="help-new-button" className="rounded-full h-11 px-6 bg-rose-600 hover:bg-rose-700"><Plus className="h-4 w-4 mr-1" />Request help</Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Ask the brotherhood for help</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="help-form">
          <div className="sm:col-span-2 space-y-1.5"><Label>Title</Label><Input required value={f.title} onChange={set("title")} placeholder="Urgent surgery for ..." data-testid="help-title-input" /></div>
          <div className="space-y-1.5"><Label>Who needs help</Label><Input required value={f.beneficiary} onChange={set("beneficiary")} placeholder="Name, batch / relation" data-testid="help-beneficiary-input" /></div>
          <div className="space-y-1.5"><Label>Amount needed (₹)</Label><Input required type="number" min="1" value={f.target_amount} onChange={set("target_amount")} data-testid="help-target-input" /></div>
          <div className="space-y-1.5"><Label>Category</Label>
            <select value={f.category} onChange={set("category")} data-testid="help-category-select" className="h-9 w-full rounded-md border border-input px-3 text-sm">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <div className="space-y-1.5"><Label>Urgency</Label>
            <select value={f.urgency} onChange={set("urgency")} data-testid="help-urgency-select" className="h-9 w-full rounded-md border border-input px-3 text-sm">
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option>
            </select>
          </div>
          <div className="sm:col-span-2 space-y-1.5"><Label>What happened & how the money will be used</Label><Textarea required rows={4} value={f.description} onChange={set("description")} data-testid="help-description-input" /></div>
          <div className="space-y-1.5"><Label>UPI ID for direct payment</Label><Input value={f.upi_id} onChange={set("upi_id")} placeholder="name@okhdfcbank" data-testid="help-upi-input" /></div>
          <div className="space-y-1.5"><Label>Contact person</Label><Input value={f.contact} onChange={set("contact")} placeholder="Name + phone" data-testid="help-contact-input" /></div>
          <div className="sm:col-span-2 space-y-1.5"><Label>Bank details (A/C, IFSC, name)</Label><Input value={f.bank_details} onChange={set("bank_details")} data-testid="help-bank-input" /></div>
          <Button type="submit" data-testid="help-submit-button" className="sm:col-span-2 rounded-full h-11 bg-stone-900 hover:bg-stone-800">Post request</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const HelpCard = ({ r }) => {
  const pct = Math.min(100, (r.pledged / r.target_amount) * 100);
  return (
    <Link to={`/help/${r.id}`} data-testid={`help-card-${r.id}`} className="bg-white border border-stone-200 rounded-lg p-6 card-lift flex flex-col">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full ${URGENCY[r.urgency] || URGENCY.medium}`}>{r.urgency}</span>
        <span className="text-xs text-stone-500">{r.category}</span>
        {r.status === "closed" && <span className="text-[11px] font-mono uppercase px-2.5 py-1 rounded-full bg-stone-200 text-stone-700">Closed</span>}
      </div>
      <h3 className="text-xl font-bold mt-4 text-stone-900 leading-snug">{r.title}</h3>
      <p className="text-sm text-stone-600 mt-2 line-clamp-2">{r.description}</p>
      <div className="mt-auto pt-6">
        <Progress value={pct} className="h-2 bg-stone-100 [&>div]:bg-rose-600" />
        <div className="flex justify-between mt-2 text-sm">
          <span><span className="font-bold text-stone-900">{formatINR(r.pledged)}</span> <span className="text-stone-500">of {formatINR(r.target_amount)}</span></span>
          <span className="text-stone-500">{r.pledge_count} pledges</span>
        </div>
        <p className="text-xs text-stone-400 mt-3">By {r.author?.name} • {formatDate(r.created_at)}</p>
      </div>
    </Link>
  );
};

export default function Help() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("open");
  const load = () => api.get("/help", { params: { status: tab } }).then(({ data }) => setItems(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div>
      <PageHeader eyebrow="Help & mutual aid" title="When one of us falls, we all lift." description="Pledge what you can and pay directly to the family via UPI or bank. The platform never touches the money." action={<NewHelpDialog onCreated={load} />} />
      <div className="flex gap-2 mb-8">
        {["open", "closed"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`help-tab-${t}`} className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:bg-stone-100"}`}>{t}</button>
        ))}
      </div>
      {!items ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={HeartHandshake} title={tab === "open" ? "No active requests" : "No closed requests"} text="Hopefully everyone is doing well." testid="help-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="help-grid">{items.map((r) => <HelpCard key={r.id} r={r} />)}</div>
      )}
    </div>
  );
}
